import { impactLevels, impactTypes, riskLevels, sdgAreas } from "./profileOptions";

const ToggleCard = ({ active, title, description, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        className={`rounded-lg border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-sm ${
            active ? "border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200" : "border-slate-200 bg-white"
        }`}
    >
        <div className="text-sm font-bold text-slate-950">{title}</div>
        <div className="mt-2 text-sm leading-5 text-slate-500">{description}</div>
    </button>
);

const SectionTitle = ({ label, title, description }) => (
    <div className="mb-4">
        <div className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">{label}</div>
        <h2 className="mt-1 text-xl font-bold text-slate-950">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>
    </div>
);

export const emptyProfile = {
    name: "",
    description: "",
    risk_level: "balanced",
    impact_level: "aligned",
    impact_types: ["Climate transition"],
    sdg_areas: [3, 7, 13],
};

export const normaliseProfile = (profile = {}) => ({
    ...emptyProfile,
    ...profile,
    impact_types: Array.isArray(profile.impact_types) ? profile.impact_types : emptyProfile.impact_types,
    sdg_areas: Array.isArray(profile.sdg_areas)
        ? profile.sdg_areas.map((value) => Number(value))
        : emptyProfile.sdg_areas,
});

export const InvestmentProfileForm = ({ profile, onChange, onSubmit, submitLabel = "Save", saving = false }) => {
    const updateField = (field, value) => {
        onChange({ ...profile, [field]: value });
    };

    const toggleValue = (field, value) => {
        const current = profile[field] || [];
        updateField(field, current.includes(value)
            ? current.filter((item) => item !== value)
            : [...current, value]
        );
    };

    return (
        <div className="space-y-5">
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <SectionTitle
                    label="Identity"
                    title="Name the mandate"
                    description="Give this profile a clear investor-facing name and a short explanation of what it is trying to achieve."
                />
                <div className="grid gap-4 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1fr)]">
                    <div>
                        <label className="text-sm font-bold text-slate-700" htmlFor="name">Profile name</label>
                        <input
                            id="name"
                            className="mt-2 w-full rounded-lg border border-slate-200 px-4 py-3 text-slate-950 outline-none focus:border-emerald-400"
                            value={profile.name || ""}
                            onChange={(event) => updateField("name", event.target.value)}
                            placeholder="Balanced impact investor"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-bold text-slate-700" htmlFor="description">Profile description</label>
                        <textarea
                            id="description"
                            className="mt-2 min-h-[110px] w-full rounded-lg border border-slate-200 px-4 py-3 text-slate-950 outline-none focus:border-emerald-400"
                            value={profile.description || ""}
                            onChange={(event) => updateField("description", event.target.value)}
                            placeholder="Describe the investor preference, constraints, and values."
                        />
                    </div>
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <SectionTitle
                    label="Risk"
                    title="Risk appetite"
                    description="Set the market and portfolio risk posture this profile is comfortable with."
                />
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    {riskLevels.map((level) => (
                        <ToggleCard
                            key={level.id}
                            active={profile.risk_level === level.id}
                            title={level.title}
                            description={level.description}
                            onClick={() => updateField("risk_level", level.id)}
                        />
                    ))}
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <SectionTitle
                    label="Impact"
                    title="Level and type of impact"
                    description="Choose how strongly impact should influence the search, then select the impact themes that matter."
                />
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    {impactLevels.map((level) => (
                        <ToggleCard
                            key={level.id}
                            active={profile.impact_level === level.id}
                            title={level.title}
                            description={level.description}
                            onClick={() => updateField("impact_level", level.id)}
                        />
                    ))}
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                    {impactTypes.map((type) => (
                        <button
                            key={type}
                            type="button"
                            onClick={() => toggleValue("impact_types", type)}
                            className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                                profile.impact_types?.includes(type)
                                    ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                            }`}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <SectionTitle
                    label="SDGs"
                    title="17 Sustainable Development Goal areas"
                    description="Select the SDG areas this profile should emphasise when assessing impact fit."
                />
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {sdgAreas.map((sdg) => {
                        const active = profile.sdg_areas?.includes(sdg.id);
                        return (
                            <button
                                key={sdg.id}
                                type="button"
                                onClick={() => toggleValue("sdg_areas", sdg.id)}
                                className={`flex items-center gap-3 rounded-lg border p-3 text-left transition hover:-translate-y-0.5 ${
                                    active ? "border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200" : "border-slate-200 bg-white"
                                }`}
                            >
                                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-sm font-bold text-white ${sdg.color}`}>
                                    {sdg.id}
                                </span>
                                <span className="text-sm font-bold text-slate-800">{sdg.title}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={onSubmit}
                    disabled={saving}
                    className="rounded-lg bg-emerald-600 px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700 disabled:bg-slate-300"
                >
                    {saving ? "Saving..." : submitLabel}
                </button>
            </div>
        </div>
    );
};
